import { useState } from 'react';
import { ChevronDown, ChevronUp, Star, Split, ArrowRight } from 'lucide-react';
import { cn } from '@utils/classNames';
import { formatBytes } from '@utils/format';
import { getGroupColor } from '@utils/fileGrouping';
import { UploadFileGroup, UploadVersionType } from '../types';

const VERSION_TYPES: UploadVersionType[] = ['tagged', 'clean', 'wav', 'mp3', 'stems', 'master'];

export interface UploadGroupCardProps {
  group: UploadFileGroup;
  mode: 'grouping' | 'metadata' | 'review';
  expanded?: boolean;
  onToggleExpand?: () => void;
  getFileSize: (fileName: string) => number;
  onSetMainFile?: (fileName: string) => void;
  onSetVersionType?: (fileName: string, type: UploadVersionType) => void;
  onSplitFile?: (fileName: string) => void;
  onMoveFile?: (fileName: string, targetGroupId: string) => void;
  otherGroups?: UploadFileGroup[];
  children?: React.ReactNode;
}

export const UploadGroupCard: React.FC<UploadGroupCardProps> = ({
  group,
  mode,
  expanded: controlledExpanded,
  onToggleExpand,
  getFileSize,
  onSetMainFile,
  onSetVersionType,
  onSplitFile,
  onMoveFile,
  otherGroups = [],
  children,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(true);
  const expanded = controlledExpanded ?? internalExpanded;
  const toggleExpand = onToggleExpand ?? (() => setInternalExpanded((v) => !v));
  const color = getGroupColor(group.id);

  return (
    <div
      className="rounded-lg lg:rounded-xl border border-white/10 bg-white/5 overflow-hidden"
      style={{ borderLeftWidth: 3, borderLeftColor: color }}
    >
      <button
        type="button"
        onClick={toggleExpand}
        className="w-full flex items-center justify-between gap-2 p-3 sm:p-3.5 min-h-[44px] text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <p className="font-inter font-bold text-white text-sm sm:text-base truncate">
            {group.baseName}
          </p>
          <p className="text-white/50 text-xs sm:text-sm font-inter">
            {group.fileNames.length} file{group.fileNames.length !== 1 ? 's' : ''}
            {group.bpm && mode !== 'grouping' && ` · ${group.bpm} BPM`}
            {group.musicalKey && mode !== 'grouping' && ` · ${group.musicalKey}`}
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-white/50 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-white/50 flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 sm:px-3.5 sm:pb-3.5 space-y-2 border-t border-white/5 pt-2">
          {group.fileNames.map((fileName) => {
            const isMain = fileName === group.mainFileName;
            const versionType = group.versionTypes[fileName];

            return (
              <div
                key={fileName}
                className={cn(
                  'flex flex-col gap-2 p-2.5 rounded-lg border',
                  isMain ? 'bg-primary/10 border-primary/30' : 'bg-white/5 border-white/10'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-white text-xs sm:text-sm font-inter font-medium truncate">
                        {fileName}
                      </p>
                      {isMain && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-primary/20 text-primary text-[10px] font-inter font-semibold">
                          <Star className="w-2.5 h-2.5" />
                          Main
                        </span>
                      )}
                      {versionType && (
                        <span className="px-1.5 py-0.5 rounded bg-white/10 text-white/70 text-[10px] font-inter capitalize">
                          {versionType}
                        </span>
                      )}
                    </div>
                    <p className="text-white/40 text-[10px] sm:text-xs font-inter mt-0.5">
                      {formatBytes(getFileSize(fileName))}
                    </p>
                  </div>

                  {mode === 'grouping' && (
                    <div className="flex flex-wrap gap-1 flex-shrink-0">
                      {!isMain && onSetMainFile && (
                        <button
                          type="button"
                          onClick={() => onSetMainFile(fileName)}
                          className="px-2 py-1.5 min-h-[36px] rounded-lg bg-white/10 text-white/70 hover:bg-white/20 text-[10px] sm:text-xs font-inter"
                        >
                          Set main
                        </button>
                      )}
                      {group.fileNames.length > 1 && onSplitFile && (
                        <button
                          type="button"
                          onClick={() => onSplitFile(fileName)}
                          className="p-1.5 min-h-[36px] min-w-[36px] rounded-lg bg-white/10 text-white/70 hover:bg-white/20 flex items-center justify-center"
                          aria-label="Split from group"
                        >
                          <Split className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {mode === 'grouping' && onSetVersionType && (
                  <div className="flex flex-wrap gap-1">
                    {VERSION_TYPES.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => onSetVersionType(fileName, versionType === type ? null : type)}
                        className={cn(
                          'px-2 py-1 min-h-[32px] rounded-md text-[10px] sm:text-xs font-inter capitalize transition-all',
                          versionType === type
                            ? 'bg-primary/20 text-primary border border-primary/30'
                            : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'
                        )}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                )}

                {mode === 'grouping' && onMoveFile && otherGroups.length > 0 && (
                  <div className="flex flex-wrap gap-1 items-center">
                    <ArrowRight className="w-3 h-3 text-white/40 flex-shrink-0" />
                    {otherGroups.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => onMoveFile(fileName, g.id)}
                        className="px-2 py-1 min-h-[32px] rounded-md bg-white/5 text-white/60 hover:bg-white/10 text-[10px] sm:text-xs font-inter truncate max-w-[120px]"
                      >
                        → {g.baseName}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {children}
        </div>
      )}
    </div>
  );
};
